import type { HotelDetailData } from '../types/hotels.presentation';

const STORAGE_PREFIX = 'hotels.detail.';

/** Hotel detail stored from search results is fresh for 5 minutes (aligned with hotel search TTL). */
export const HOTEL_DETAIL_TTL_MS = 5 * 60 * 1000;

interface StoredDetail {
  savedAt: number;
  detail: HotelDetailData;
}

export type HotelDetailState =
  | { status: 'available'; detail: HotelDetailData }
  | { status: 'expired'; detail: HotelDetailData }
  | { status: 'missing' };

/**
 * Checks whether a stored hotel detail has enough revalidation metadata
 * to call the availability endpoint. Old envelopes without revalidation
 * fields are treated as non-revalidatable.
 */
export function canRevalidateDetail(detail: HotelDetailData): boolean {
  const r = detail.revalidation;
  return !!(
    r &&
    r.searchToken &&
    r.offerToken &&
    Array.isArray(r.rateKeys) &&
    r.rateKeys.length > 0
  );
}

/**
 * Builds an opaque cache key that disambiguates same-hotel entries from different
 * search/supplier contexts. Passed via URL so the detail page reads the exact
 * stored envelope.
 *
 * Falls back to bare hotelId when neither supplier nor searchToken is available
 * (backwards-compatible with old single-key envelopes).
 */
export function buildCacheKey(detail: HotelDetailData): string {
  const supplier = detail.supplier || '';
  const searchToken = detail.revalidation?.searchToken || '';
  if (!supplier && !searchToken) return detail.hotelId;
  return `${detail.hotelId}|${supplier}|${searchToken}`;
}

function storageKey(hotelId: string, cacheKey?: string): string {
  const suffix = cacheKey || hotelId;
  return `${STORAGE_PREFIX}${suffix}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage ?? null;
}

function isExpired(savedAt: number): boolean {
  return Date.now() - savedAt >= HOTEL_DETAIL_TTL_MS;
}

function cleanupExpiredEntries(): void {
  const storage = getStorage();
  if (!storage) return;

  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith(STORAGE_PREFIX)) continue;

    const raw = storage.getItem(key);
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed.savedAt === 'number') {
        if (isExpired(parsed.savedAt)) {
          keysToRemove.push(key);
        }
      } else {
        keysToRemove.push(key);
      }
    } catch {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}

function removeAllDetailEntries(storage: Storage): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keysToRemove.push(key);
  }
  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}

export function saveHotelDetail(detail: HotelDetailData): void {
  const storage = getStorage();
  if (!storage || !detail.hotelId) return;

  cleanupExpiredEntries();

  const envelope: StoredDetail = {
    savedAt: Date.now(),
    detail,
  };
  const key = storageKey(detail.hotelId, buildCacheKey(detail));
  const value = JSON.stringify(envelope);

  try {
    storage.setItem(key, value);
  } catch {
    // Quota exceeded. Evict every cached detail (all are short-lived) and
    // retry once; if the storage is still full, skip caching — the detail
    // page can revalidate against the API.
    removeAllDetailEntries(storage);
    try {
      storage.setItem(key, value);
    } catch {
      // Storage unavailable or entry larger than the remaining quota.
    }
  }
}

function parseStoredDetail(raw: string | null, hotelId: string): StoredDetail | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredDetail>;
    if (
      typeof parsed.savedAt !== 'number' ||
      parsed.detail?.hotelId !== hotelId ||
      !parsed.detail.name ||
      !Array.isArray(parsed.detail.roomOptions)
    ) {
      return null;
    }
    return parsed as StoredDetail;
  } catch {
    return null;
  }
}

function findLatestEnvelopeByHotelId(hotelId: string): StoredDetail | null {
  const storage = getStorage();
  if (!storage || !hotelId) return null;

  let latest: StoredDetail | null = null;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith(STORAGE_PREFIX)) continue;

    const stored = parseStoredDetail(storage.getItem(key), hotelId);
    if (!stored) continue;

    if (!latest || stored.savedAt > latest.savedAt) {
      latest = stored;
    }
  }

  return latest;
}

function readEnvelope(hotelId: string, cacheKey?: string): StoredDetail | null {
  const storage = getStorage();
  if (!storage || !hotelId) return null;

  if (cacheKey) {
    const raw = storage.getItem(storageKey(hotelId, cacheKey));
    const stored = parseStoredDetail(raw, hotelId);
    if (stored) return stored;
  }

  // Fallback: try the legacy hotelId-only key for backwards compatibility.
  const legacyRaw = storage.getItem(storageKey(hotelId));
  const legacyStored = parseStoredDetail(legacyRaw, hotelId);
  if (legacyStored) return legacyStored;

  // Last resort: keep detail pages resilient when the exact composite key is
  // unavailable but another non-malformed detail snapshot for this hotel still
  // exists in the detail cache. TTL is still enforced by callers.
  return findLatestEnvelopeByHotelId(hotelId);
}

/**
 * Returns the number of milliseconds until the stored detail expires, or
 * `null` when no valid envelope exists. Returns 0 when already expired.
 */
export function getHotelDetailRemainingMs(hotelId: string, cacheKey?: string): number | null {
  const stored = readEnvelope(hotelId, cacheKey);
  if (!stored) return null;

  const elapsed = Date.now() - stored.savedAt;
  return Math.max(0, HOTEL_DETAIL_TTL_MS - elapsed);
}

export function readHotelDetail(hotelId: string, cacheKey?: string): HotelDetailData | null {
  const stored = readEnvelope(hotelId, cacheKey);
  if (!stored) return null;

  if (Date.now() - stored.savedAt >= HOTEL_DETAIL_TTL_MS) return null;

  return stored.detail;
}

export function readHotelDetailState(hotelId: string, cacheKey?: string): HotelDetailState {
  const stored = readEnvelope(hotelId, cacheKey);
  if (!stored) return { status: 'missing' };

  if (Date.now() - stored.savedAt >= HOTEL_DETAIL_TTL_MS) {
    return { status: 'expired', detail: stored.detail };
  }

  return { status: 'available', detail: stored.detail };
}
